/**
 * StaticHome.tsx — صفحة إتقان الواحدة الساكنة
 *
 * مبنية وفق:
 *   THE_MASTER_COMMAND.md + THE_SOUL_LAYER.md + THE_PROFESSIONAL_POLISH.md
 *   + THE_FRAME_ELEVATION.md + THE_TRUST_ARCHITECTURE.md
 *
 * البنية الجديدة (B2B funnel — 14 قسم):
 *   01 Hero · 02 Logos · 03 Services · 04 Industries · 05 Framework
 *   06 Stats · 07 Portfolio · 08 Featured Case · 09 Testimonials
 *   10 Team · 11 Awards · 12 Packages · 13 FAQ · 14 Contact
 *
 * قوانين سارية:
 *   • Zero-JS for content · Brand Tokens locked · Dark only · إتقان only
 *   • Law #6 — Arabic Human Voice (no banned phrases, specifics > abstractions)
 */

import { siteConfig } from "@/config/siteConfig";

const itqanLogo = "/brand/itqan-logo.png";

/* ═══════════════════════════ DATA (static) ═══════════════════════════ */

const NAV_ITEMS = [
  { label: "الخدمات", href: "#services" },
  { label: "القطاعات", href: "#industries" },
  { label: "الإطار", href: "#framework" },
  { label: "الأعمال", href: "#portfolio" },
  { label: "آراء العملاء", href: "#testimonials" },
  { label: "من نحن", href: "#team" },
  { label: "الأسئلة", href: "#faq" },
  { label: "المدونة", href: "/blog" },
  { label: "تواصل", href: "#contact" },
] as const;

const STATS = [
  { value: "+4,700", label: "مشروع سعودي مُنفّذ منذ ٢٠١٨" },
  { value: "98%", label: "نسبة العملاء الذين جدّدوا معنا" },
  { value: "+3,000", label: "علامة تجارية بنينا حضورها" },
] as const;

const SERVICES = [
  {
    n: "٠١",
    title: "تصميم تجربة المستخدم",
    description: "نُعيد تصميم رحلة الشراء بناءً على سلوك المستخدم السعودي الحقيقي — لا قوالب جاهزة.",
    tags: ["تجربة المستخدم", "هيكلة", "نماذج أولية"],
  },
  {
    n: "٠٢",
    title: "استراتيجية رقمية",
    description: "نضع خطة نمو مكتوبة بمعايير قياس واضحة، ربعية ومراجعة شهرية.",
    tags: ["خارطة طريق", "استراتيجية الإطلاق", "تموضع البراند"],
  },
  {
    n: "٠٣",
    title: "تطوير ويب متقدم",
    description: "نبني واجهات سريعة بـ Core Web Vitals خضراء، ودعم عربي كامل من الصفر.",
    tags: ["Next.js", "أداء", "دعم العربية"],
  },
  {
    n: "٠٤",
    title: "إنشاء وإدارة المتاجر الإلكترونية",
    description: "ندير متاجر سلة وزد كاملة — كتالوج، شحن، دفع، عمليات، تقارير.",
    tags: ["سلة", "زد", "حلول مخصصة"],
  },
  {
    n: "٠٥",
    title: "إطلاق وإدارة الحملات الإعلانية",
    description: "ندير ميزانيات Meta وGoogle وTikTok بمتوسط ROAS ٣٫٨× خلال ٩٠ يومًا.",
    tags: ["إعلانات أداء", "تتبع تحويلات", "تحسين كلفة"],
  },
  {
    n: "٠٦",
    title: "تسويق المؤثرين",
    description: "نختار المؤثر بمنهجية مبنية على البيانات لا بالشهرة، ونقيس كل ريال يُصرف.",
    tags: ["Spark Ads", "محتوى براندد", "مكافحة احتيال"],
  },
  {
    n: "٠٧",
    title: "تحسين معدل التحويل",
    description: "نُجري اختبار A/B أسبوعيًا، ونرفع CVR بمعدل +٢٫١pp خلال ٩٠ يومًا.",
    tags: ["A/B Testing", "تحليل سلوك", "Heatmaps"],
  },
  {
    n: "٠٨",
    title: "تحسين محركات البحث",
    description: "SEO تقني ومحتوى عربي يلتقط طلبات السوق السعودي تحديدًا — لا ترجمة آلية.",
    tags: ["SEO تقني", "محتوى عربي", "Core Web Vitals"],
  },
  {
    n: "٠٩",
    title: "إدارة العلاقات والاحتفاظ",
    description: "حملات بريد ورسائل وإشعارات ترفع قيمة العميل مدى الحياة بنسبة ٤٧٪.",
    tags: ["Email", "WhatsApp", "Loyalty"],
  },
] as const;

/* — قطاعات نخدمها (٩) — */
const INDUSTRIES = [
  { n: "٠١", name: "الأزياء والموضة", detail: "٢٢ علامة سعودية · متوسط نمو ٢١٠٪" },
  { n: "٠٢", name: "التجميل والعناية الشخصية", detail: "١٤ علامة · متوسط ROAS ٤٫٢×" },
  { n: "٠٣", name: "الأطعمة والمشروبات", detail: "٩ علامات · توسّع لخمس مدن" },
  { n: "٠٤", name: "الإلكترونيات والتقنية", detail: "٧ متاجر · انخفاض CAC ٣٨٪" },
  { n: "٠٥", name: "العطور والعود", detail: "١١ علامة · نمو ٥× في الموسم" },
  { n: "٠٦", name: "مستلزمات الأطفال والأمومة", detail: "٦ علامات · رضا متكرر +٧٢٪" },
  { n: "٠٧", name: "الرياضة واللياقة", detail: "٤ علامات · إطلاق متاجر D2C" },
  { n: "٠٨", name: "المنزل والأثاث", detail: "٥ علامات · رفع AOV +٤٧٪" },
  { n: "٠٩", name: "الخدمات الاحترافية والاستشارات", detail: "٨ شركات · توليد B2B leads" },
] as const;

/* — الإطار التشغيلي لإتقان: ٤ مراحل — */
const FRAMEWORK = [
  {
    n: "٠١",
    tag: "الأسبوع ١-٢",
    title: "اكتشاف وتشخيص",
    description:
      "ورشة تشخيصية نفهم فيها أهدافك، نحلل سوقك، ونحدد الفرص. نُسلّم تقريرًا تفصيليًا في ١٠ أيام عمل.",
    deliverables: ["تحليل المنافسين", "تشخيص الحضور الرقمي", "خريطة الفرص"],
  },
  {
    n: "٠٢",
    tag: "الأسبوع ٣-٤",
    title: "استراتيجية وخارطة طريق",
    description:
      "نبني استراتيجية مكتوبة بمعايير قياس واضحة، وأولويات أسبوعية للأشهر الثلاثة الأولى.",
    deliverables: ["خارطة طريق ٩٠ يومًا", "KPIs ربعية", "خطة محتوى وإعلانات"],
  },
  {
    n: "٠٣",
    tag: "الأسبوع ٥-١٠",
    title: "تنفيذ بإيقاع أسبوعي",
    description:
      "إطلاقات أسبوعية، اختبارات A/B مستمرة، وتحسين مستند للبيانات لا للتخمين.",
    deliverables: ["Sprints أسبوعية", "تجارب A/B", "Stand-up أسبوعي معك"],
  },
  {
    n: "٠٤",
    tag: "الأسبوع ١١-١٢ ثم شهريًا",
    title: "قياس وتطوير",
    description:
      "تقارير شفافة كل أسبوع، لقاء استراتيجي شهري، وتعديل المسار حسب ما تثبته الأرقام.",
    deliverables: ["تقرير شهري", "Dashboard لحظي", "خطة الربع التالي"],
  },
] as const;

const PORTFOLIO = [
  {
    cat: "أزياء", year: "2024", title: "متجر أزياء سعودي",
    summary: "أعدنا تصميم تجربة الشراء وحسّنا سرعة الموقع، فارتفع معدل التحويل من 1.2% إلى 3.4% خلال 90 يومًا.",
    metric: "+220%", metricLabel: "في معدل التحويل",
  },
  {
    cat: "تعليم", year: "2024", title: "منصة تعليم رقمي",
    summary: "أطلقنا حملات أداء شاملة وحسّنا صفحات الهبوط، فانخفض CAC بنسبة 38% مع نمو التسجيلات 3 أضعاف.",
    metric: "×3", metricLabel: "نمو التسجيلات",
  },
  {
    cat: "صحة", year: "2023", title: "تطبيق صحة رقمية",
    summary: "بنينا واجهة سريعة مع تكامل API آمن، وحسّنا تجربة الحجز فارتفع رضا المستخدم لـ 4.8/5.",
    metric: "4.8★", metricLabel: "تقييم المستخدم",
  },
  {
    cat: "تجزئة", year: "2024", title: "متجر مستلزمات أطفال",
    summary: "أعدنا هيكلة الكتالوج وأطلقنا برنامج ولاء، فارتفع متوسط قيمة الطلب 47% ومعدل الشراء المتكرر 2.4 ضعف.",
    metric: "+47%", metricLabel: "متوسط قيمة الطلب",
  },
  {
    cat: "فاخر", year: "2023", title: "علامة عطور فاخرة",
    summary: "صمّمنا متجرًا يعكس فخامة العلامة، مع تصوير منتجات احترافي ومحتوى مستخدم، فارتفعت المبيعات 5 أضعاف.",
    metric: "×5", metricLabel: "نمو المبيعات",
  },
  {
    cat: "خدمات", year: "2023", title: "شركة خدمات احترافية",
    summary: "بنينا موقعًا مع نظام حجز ذكي وحسّنا SEO المحلي، فارتفعت الزيارات العضوية 280% خلال 6 أشهر.",
    metric: "+280%", metricLabel: "الزيارات العضوية",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "فريق إتقان فهم علامتنا قبل أن نشرحها. سلّموا متجرًا يعكس هويتنا تمامًا، والنتائج تحدّثت عن نفسها.",
    name: "سارة العتيبي",
    role: "مديرة التسويق، علامة أزياء",
    result: "+220% في معدل التحويل خلال 90 يومًا",
  },
  {
    quote:
      "أفضل قرار اتخذناه هو الشراكة مع إتقان. ضاعفنا معدل التحويل خلال ٣ أشهر، والدعم متواصل أسبوعيًا.",
    name: "محمد القحطاني",
    role: "الرئيس التنفيذي، متجر إلكترونيات",
    result: "×2 معدل التحويل خلال 90 يومًا",
  },
  {
    quote:
      "رأيت فرقًا في كل تفصيلة، من التصميم إلى الحملات. أنصح بهم لكل صاحب متجر يبحث عن شريك يفهم السوق.",
    name: "نوف الشمري",
    role: "مؤسسة، علامة عطور",
  },
];

const FOUNDERS = [
  {
    n: "٠١",
    name: "نورة العتيبي",
    role: "الشريكة المؤسِسة والرئيسة التنفيذية",
    quote: "«المتجر الإلكتروني ليس صفحة منتجات — هو علامة تُروى.»",
    initials: "ن.ع",
  },
  {
    n: "٠٢",
    name: "لينا الغامدي",
    role: "الشريكة المؤسِسة ومديرة العمليات",
    quote: "«التشغيل الجيد يحوّل الفكرة إلى عادة. والعادة إلى علامة.»",
    initials: "ل.غ",
  },
  {
    n: "٠٣",
    name: "عبدالله الدوسري",
    role: "الشريك المؤسِس والمدير التقني",
    quote: "«التقنية وحدها لا تبيع. التقنية المُحكَمة تبيع.»",
    initials: "ع.د",
  },
];

const FAQS = [
  {
    q: "ما هي منصة إتقان؟",
    a: "منصة إتقان حلٌّ سعودي متكامل لإطلاق متجرك الإلكتروني وإدارته باحتراف. نُجهِّز لك كل ما يلزم — من التصميم إلى الدفع والشحن — لتبدأ البيع مباشرة دون تعقيدات تقنية.",
  },
  {
    q: "كيف تساعدونني في إطلاق متجري؟",
    a: "لا تحتاج إلى خبرة تقنية متقدمة. نوفّر قوالب احترافية جاهزة قابلة للتخصيص، ونربطها ببوابات الدفع والشحن الموثوقة، مع لوحة تحكم سهلة لإدارة المنتجات والطلبات والعملاء. وفريقنا حاضرٌ متى احتجت إليه.",
  },
  {
    q: "هل تستوعب المنصة نمو مشروعي مستقبلًا؟",
    a: "نعم، بكل ثقة. عملاء بدأوا معنا بعشرة طلبات يوميًا، وأصبحوا اليوم يديرون مئات الطلبات بسلاسة. المنصة مصمَّمة لتنمو معك — سواء أكان لديك عشرة عملاء أم عشرة آلاف — ونساعدك لاحقًا على التوسع إلى مناطق ودول جديدة.",
  },
  {
    q: "لماذا إتقان دون غيرها؟",
    a: "أغلب المنصات إمّا معقدة على المبتدئين أو محدودة للمحترفين. جمعنا الاثنين معًا: سهولة استخدام حقيقية مع أدوات قوية. دعمنا بالعربية ومستمر، وسجلّنا يتحدث: أكثر من ٤٬٧٠٠ مشروع منذ ٢٠١٨. لا نُجرِّب عليك — نحن نعرف ما نفعله.",
  },
  {
    q: "كم تستغرق مدة إطلاق المتجر؟",
    a: "متوسط مدة الإطلاق من أسبوعين إلى أربعة أسابيع، بحسب تعقيد المشروع وحجم الكتالوج. نمنحك جدولًا زمنيًا واضحًا منذ اليوم الأول، مع نقاط مراجعة دورية.",
  },
  {
    q: "هل تقدمون دعمًا بعد الإطلاق؟",
    a: "نعم، الدعم جزءٌ أصيل من شراكتنا. نُقدِّم خططًا مرنة للدعم الفني والصيانة والتطوير المستمر بعد الإطلاق، مع فريق متخصص مُتاح للرد على استفساراتك.",
  },
  {
    q: "هل يمكنني تعديل تصميم المتجر لاحقًا؟",
    a: "بالتأكيد. القوالب التي نستخدمها مرنة وقابلة للتخصيص في أي وقت. تستطيع تعديل الألوان والخطوط والمحتوى بنفسك من لوحة التحكم، أو نُساعدك في التعديلات الكبيرة.",
  },
  {
    q: "كم تبدأ تكلفة الخدمات؟",
    a: "تتفاوت التكلفة بحسب الباقة والمتطلبات. تواصل معنا للحصول على عرض سعر مخصص يناسب احتياجاتك وميزانيتك. نُقدِّم استشارة مجانية أولية لتحديد الأنسب لك.",
  },
];

/* ═══════════════════════════ JSON-LD ═══════════════════════════ */

export const JSON_LD_BLOCKS = [
  {
    "@context": "https://schema.org",
    "@type": ["Organization", "ProfessionalService"],
    "@id": "https://itqan.agency/#organization",
    name: "إتقان",
    alternateName: ["itqan", "itqan.agency"],
    url: "https://itqan.agency/",
    logo: "https://itqan.agency/brand/itqan-logo.svg",
    image: "https://itqan.agency/og-image.jpg",
    description:
      "منصة إتقان وكالة سعودية متكاملة لإنشاء وإدارة المتاجر الإلكترونية والتسويق الرقمي منذ 2018.",
    address: {
      "@type": "PostalAddress",
      addressCountry: "SA",
      addressRegion: "الرياض",
      addressLocality: "الرياض",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+966535632482",
        email: "Care@itQan.Agency",
        contactType: "customer service",
        areaServed: "SA",
        availableLanguage: ["Arabic", "English"],
      },
    ],
    sameAs: ["https://twitter.com/itqanagency"],
    priceRange: "$$",
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://itqan.agency/#website",
    url: "https://itqan.agency/",
    name: "إتقان",
    publisher: { "@id": "https://itqan.agency/#organization" },
    inLanguage: "ar-SA",
  },
  ...SERVICES.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: s.title,
    provider: { "@id": "https://itqan.agency/#organization" },
    areaServed: { "@type": "Country", name: "Saudi Arabia" },
    description: s.description,
  })),
  ...INDUSTRIES.map((i) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `تسويق رقمي وإدارة متاجر — قطاع ${i.name}`,
    provider: { "@id": "https://itqan.agency/#organization" },
    areaServed: { "@type": "Country", name: "Saudi Arabia" },
  })),
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "الرئيسية", item: "https://itqan.agency/" },
    ],
  },
];

/* ═══════════════════════════ COMPONENT ═══════════════════════════ */

const StaticHome = () => (
  <>
    {/* Skip link */}
    <a href="#main" className="skip-to-content">تخطّي إلى المحتوى</a>

    {/* ═════════════════ HEADER · Centered editorial masthead ═════════════════ */}
    <header className="site-header" role="banner">
      <div className="header-inner">
        {/* Hidden checkbox controls mobile drawer (CSS-only) */}
        <input type="checkbox" id="menu-toggle" className="menu-toggle-input" hidden />

        {/* Hamburger — mobile only */}
        <label htmlFor="menu-toggle" className="menu-btn" aria-label="فتح القائمة">
          <span className="hamburger" aria-hidden="true">
            <span></span><span></span><span></span>
          </span>
        </label>

        {/* ROW 1 — Logo, centered */}
        <a href="#main" className="brand" aria-label="إتقان — العودة إلى أعلى الصفحة">
          <img
            src={itqanLogo}
            alt=""
            className="brand-logo"
            width={56}
            height={56}
            fetchPriority="high"
            decoding="async"
          />
        </a>

        {/* ROW 2 — Navigation, centered as group with CTA */}
        <nav className="site-nav" id="site-nav" aria-label="القائمة الرئيسية">
          {NAV_ITEMS.map((item) => (
            <a key={item.href} href={item.href}>{item.label}</a>
          ))}
          <a href="#contact" className="nav-cta">
            ابدأ الآن
            <span className="nav-cta-arrow" aria-hidden="true">←</span>
          </a>
        </nav>

        {/* Backdrop for mobile drawer */}
        <label htmlFor="menu-toggle" className="menu-backdrop" aria-hidden="true"></label>
      </div>
    </header>

    {/* ═════════════════ MAIN ═════════════════ */}
    <main id="main" role="main">

      {/* ═══ 01 · HERO ═══ */}
      <section
        id="hero"
        aria-labelledby="hero-h"
        className="hero atmosphere relative overflow-hidden"
        style={{ minHeight: "88vh", paddingBlock: "80px" }}
      >
        <div className="hero-beam" aria-hidden="true"></div>
        <div className="container-x grid grid-cols-12 gap-6 lg:gap-8 items-center" style={{ minHeight: "calc(88vh - 160px)" }}>
          <span
            className="watermark-numeral t-numeral-xl hidden md:block"
            aria-hidden="true"
            style={{ top: "50%", insetInlineStart: "5%", transform: "translateY(-50%)" }}
          >
            ٠١
          </span>
          <div
            aria-hidden="true"
            className="hidden lg:block absolute"
            style={{
              insetInlineStart: "33%", top: "10%", bottom: "10%",
              width: "1px", background: "var(--border-subtle)",
            }}
          />

          <div className="col-span-12 lg:col-span-9 relative z-10">
            <div className="hero-status-row reveal-1">
              <span className="status-pill">
                <span className="status-pulse" aria-hidden="true"></span>
                نقبل {siteConfig.monthlyCapacity} مشاريع شهريًا · متبقّى {siteConfig.remainingSlots} أماكن
              </span>
              <span className="status-divider" aria-hidden="true">·</span>
              <span className="status-text">نرد خلال {siteConfig.responseTime} في ساعات العمل</span>
            </div>
            <div className="reveal-1">
              <span className="section-label">إتقان · ٠١ / ١٤ — البداية</span>
            </div>

            <h1
              id="hero-h"
              className="t-display-1 hero-headline mt-6 mb-6"
              style={{ color: "var(--text-primary)" }}
            >
              <span className="block reveal-2">
                نُطلق <span className="accent">متاجر تبيع</span>.
              </span>
              <span className="block reveal-3 mt-2">ونصنع علاماتٍ تكبر.</span>
            </h1>

            <p
              className="t-body-lg max-w-prose mb-8 reveal-3"
              style={{ color: "var(--text-secondary)" }}
            >
              منصة سعودية متخصصة في بناء وإدارة المتاجر الإلكترونية. أكثر من{" "}
              <a href="#portfolio" style={{ color: "var(--text-primary)", borderBottom: "1px solid var(--accent-primary)", textDecoration: "none" }}>
                <strong>٤٬٧٠٠ مشروع ناجح</strong>
              </a>{" "}
              منذ 2018، في أكثر من{" "}
              <a href="#industries" style={{ color: "var(--text-primary)", borderBottom: "1px solid var(--accent-primary)", textDecoration: "none" }}>
                ٩ قطاعات سعودية
              </a>.
            </p>

            <div className="flex flex-wrap items-center gap-6 reveal-4">
              <a href="#contact" className="cta-pill">
                احجز استشارة <span aria-hidden="true">←</span>
              </a>
              <a href="#portfolio" className="cta-underline">
                <span>شاهد أعمالنا</span>
                <span className="arrow" aria-hidden="true">←</span>
              </a>
            </div>
          </div>
        </div>

        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 flex items-center justify-center"
          style={{ paddingInline: "24px" }}
        >
          <div className="relative w-full" style={{ height: "1px", background: "var(--border-subtle)" }}>
            <span
              className="absolute"
              style={{
                inset: "auto 50% auto auto",
                transform: "translate(50%, -50%)",
                top: "50%",
                width: "6px", height: "6px", borderRadius: "9999px",
                background: "var(--accent-primary)",
                boxShadow: "0 0 0 4px var(--accent-glow)",
              }}
            />
          </div>
        </div>
      </section>

      {/* ═══ 02 · LOGOS WALL ═══ */}
      <section id="logos" className="logos-wall" aria-labelledby="logos-h">
        <div className="container-x">
          <div className="text-center" style={{ marginBlockEnd: "20px" }}>
            <span className="section-label">عملاؤنا · ٠٢ / ١٤ — الثقة</span>
          </div>
          <h2 id="logos-h" className="visually-hidden">علامات سعودية تثق بنا</h2>

          <p className="logos-statement">
            <span className="accent-number">+47</span>
            علامة سعودية اختارتنا لبناء حضورها الرقمي منذ ٢٠١٨.
          </p>

          <ul className="logos-list-text" aria-label="قائمة العملاء">
            <li>أزياء الرياض</li>
            <li>عطور المدينة</li>
            <li>متجر العائلة</li>
            <li>تجميل الخبر</li>
            <li>مأكولات نجد</li>
            <li>إلكترونيات جدة</li>
            <li>أثاث الدمام</li>
            <li>صحة الرياض</li>
            <li>تعليم سعودي</li>
            <li>رياضة المملكة</li>
          </ul>
        </div>
      </section>

      {/* ═══ 03 · SERVICES ═══ */}
      <section id="services" aria-labelledby="services-h" className="section-y relative overflow-hidden">
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <span className="section-label">الخدمات · ٠٣ / ١٤ — ما نُقدِّم</span>
              <h2 id="services-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
                خدماتنا
              </h2>
              <p className="t-body" style={{ color: "var(--text-secondary)" }}>
                ٩ خدمات مترابطة، طوّرناها على مدى ٤٬٧٠٠ مشروع. تختار الخدمة وحدها، أو تثق فينا لإدارة منظومتك كاملة. خبرتنا تمتد إلى{" "}
                <a href="#industries" style={{ color: "var(--accent-primary)" }}>أكثر من ٩ قطاعات سعودية</a>.
              </p>
            </div>
          </div>

          <ul className="col-span-12 lg:col-span-8 flex flex-col gap-4">
            {SERVICES.map((service) => (
              <li
                key={service.title}
                className="hairline service-item group"
                style={{ padding: "24px 28px" }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="t-heading-2 mb-2" style={{ color: "var(--text-primary)" }}>
                      {service.title}
                    </h3>
                    <p className="t-body" style={{ color: "var(--text-secondary)" }}>
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {service.tags.map((t) => (
                        <span key={t} className="service-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                  <span
                    aria-hidden="true"
                    style={{
                      fontWeight: 500, fontSize: "28px",
                      color: "var(--text-tertiary)",
                      lineHeight: 1, fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {service.n}.
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══ 04 · INDUSTRIES ═══ */}
      <section id="industries" aria-labelledby="industries-h" className="section-y relative" style={{ background: "var(--bg-secondary)" }}>
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x">
          <header>
            <span className="section-label">القطاعات · ٠٤ / ١٤ — التخصص</span>
            <h2 id="industries-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
              قطاعات نُتقن العمل بها
            </h2>
            <p className="t-body-lg max-w-prose" style={{ color: "var(--text-secondary)" }}>
              نعرف ديناميكيات كل قطاع من الداخل. لكل صناعة قواعدها في جذب العميل وتسعير المنتج وبناء الولاء — ونتعامل مع كل واحدة باستراتيجيتها.
            </p>
          </header>

          <div className="industries-grid">
            {INDUSTRIES.map((ind) => (
              <a key={ind.name} href="#contact" className="industry-card">
                <span className="industry-number">{ind.n}</span>
                <h3 className="industry-name">{ind.name}</h3>
                <p className="industry-detail">{ind.detail}</p>
                <span className="industry-link">ابدأ مشروعًا في هذا القطاع ←</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 05 · FRAMEWORK (rebranded process) ═══ */}
      <section
        id="framework"
        aria-labelledby="framework-h"
        className="section-y process-section relative overflow-hidden"
      >
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="drifting-mark" aria-hidden="true"><span>إتقان</span></div>
        <div className="container-x relative" style={{ zIndex: 1 }}>
          <span className="section-label">العملية · ٠٥ / ١٤ — الإطار</span>
          <span className="framework-mono-tag mt-4">THE ITQAN FRAMEWORK · est. 2018</span>
          <h2 id="framework-h" className="t-display-2 mb-4" style={{ color: "var(--text-primary)" }}>
            نموذج إتقان الرباعي للنمو
          </h2>
          <p className="t-body-lg max-w-prose mb-12" style={{ color: "var(--text-secondary)" }}>
            أربع مراحل مُحكمة، طوّرناها على مدى ٤٬٧٠٠ مشروع، تنقل علامتك من فكرة إلى{" "}
            <a href="#stats" style={{ color: "var(--accent-primary)" }}>نتيجة قابلة للقياس</a> خلال ٩٠ يومًا. منهجية مُختبَرة على السوق السعودي تحديدًا.
          </p>

          <ol className="flex flex-col gap-12 md:gap-20 relative">
            <span className="process-line" aria-hidden="true"></span>
            {FRAMEWORK.map((step, i) => (
              <li key={step.n} className="grid grid-cols-12 gap-6 items-start">
                <div className="col-span-3 md:col-span-2">
                  <span
                    className="step-number"
                    aria-hidden="true"
                    style={{
                      fontWeight: 900, fontSize: "clamp(56px, 8vw, 96px)",
                      color: "var(--accent-muted)", lineHeight: 1, display: "block",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {step.n}
                  </span>
                </div>
                <div className="col-span-9 md:col-span-9">
                  <span className="step-tag">{step.tag}</span>
                  <h3 className="t-heading-1 mb-3" style={{ color: "var(--text-primary)" }}>
                    {step.title}
                  </h3>
                  <p className="t-body-lg" style={{ color: "var(--text-secondary)", maxWidth: "60ch" }}>
                    {step.description}
                  </p>
                  <ul className="step-deliverables">
                    {step.deliverables.map((d) => <li key={d}>{d}</li>)}
                  </ul>
                  {i < FRAMEWORK.length - 1 && <div className="section-rule mt-12" />}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ═══ 06 · STATS ═══ */}
      <section id="stats" aria-labelledby="stats-h" className="section-y relative" style={{ background: "var(--bg-secondary)" }}>
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x">
          <span className="section-label">الأرقام · ٠٦ / ١٤ — الأثر</span>
          <h2 id="stats-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
            أرقام نقولها بلا تجميل
          </h2>
          <p className="t-body-lg max-w-prose mb-8" style={{ color: "var(--text-secondary)" }}>
            ما تراه أدناه ليس وعدًا — بل سجلٌّ يمتد منذ ٢٠١٨.{" "}
            <a href="#featured-case" style={{ color: "var(--accent-primary)" }}>اقرأ تفاصيل دراسة حالة واحدة</a> لتفهم كيف نصل إلى هذه الأرقام.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {STATS.map((s) => (
              <div key={s.label} className="stat-block hairline text-center md:text-start">
                <div
                  className="stat-number"
                  style={{
                    fontWeight: 900, fontSize: "clamp(44px, 6vw, 56px)",
                    letterSpacing: "-0.01em", lineHeight: 1,
                  }}
                >
                  {s.value}
                </div>
                <div className="t-label mt-3" style={{ color: "var(--text-tertiary)", flexBasis: "100%" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🌌 Ticker strip */}
      <div className="ticker" aria-hidden="true">
        <div className="ticker-track">
          <span>تسويق رقمي</span><span>·</span>
          <span>إدارة متاجر</span><span>·</span>
          <span>SEO تقني</span><span>·</span>
          <span>سوشيال ميديا</span><span>·</span>
          <span>إعلانات أداء</span><span>·</span>
          <span>المملكة العربية السعودية</span><span>·</span>
          <span>تسويق رقمي</span><span>·</span>
          <span>إدارة متاجر</span><span>·</span>
          <span>SEO تقني</span><span>·</span>
          <span>سوشيال ميديا</span><span>·</span>
          <span>إعلانات أداء</span><span>·</span>
          <span>المملكة العربية السعودية</span><span>·</span>
        </div>
      </div>

      {/* ═══ 07 · PORTFOLIO ═══ */}
      <section id="portfolio" aria-labelledby="portfolio-h" className="section-y relative">
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x">
          <span className="section-label">الأعمال · ٠٧ / ١٤ — البرهان</span>
          <h2 id="portfolio-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
            أعمالنا
          </h2>
          <p className="t-body-lg max-w-prose mb-12" style={{ color: "var(--text-secondary)" }}>
            +40 مشروع سعودي — متوسط نتائجنا خلال ٩٠ يومًا: +2.1pp CVR، 3.8× ROAS، -32% CAC.
          </p>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PORTFOLIO.map((p, i) => (
              <li
                key={p.title}
                className={`hairline portfolio-card ${i % 4 === 3 ? "lg:col-span-2" : ""}`}
                style={{ padding: "28px" }}
              >
                <div className="t-label mb-4" style={{ color: "var(--text-tertiary)" }}>
                  {p.cat} · {p.year}
                </div>
                <h3 className="t-heading-3 mb-3" style={{ color: "var(--text-primary)" }}>
                  {p.title}
                </h3>
                <p className="t-body-sm mb-6" style={{ color: "var(--text-secondary)" }}>
                  {p.summary}
                </p>
                <div
                  className="result-number"
                  style={{
                    fontWeight: 900, fontSize: "clamp(36px, 5vw, 48px)",
                    letterSpacing: "-0.01em", lineHeight: 1,
                  }}
                >
                  {p.metric}
                </div>
                <div className="t-label mt-2" style={{ color: "var(--text-tertiary)" }}>
                  {p.metricLabel}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══ 08 · FEATURED CASE STUDY ═══ */}
      <section id="featured-case" aria-labelledby="case-h" className="section-y relative" style={{ background: "var(--bg-secondary)" }}>
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x">
          <header className="text-center" style={{ maxWidth: "720px", marginInline: "auto" }}>
            <span className="section-label">دراسة حالة · ٠٨ / ١٤ — العمق</span>
            <h2 id="case-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
              من ١٫٢٪ إلى ٣٫٤٪ تحويلًا — كيف؟
            </h2>
            <p className="t-body-lg" style={{ color: "var(--text-secondary)" }}>
              متجر أزياء سعودي ضاعف معدل تحويله ٢٫٨ مرة في ٩٠ يومًا. الأرقام، التحديات، القرارات — بالتفصيل.
            </p>
          </header>

          <article className="case-article">
            <div className="case-meta">
              <span className="case-tag">أزياء</span>
              <span className="case-tag">ربع رابع ٢٠٢٤</span>
              <span className="case-tag">٩٠ يومًا</span>
            </div>

            <div className="case-block">
              <h3 className="case-block-title">الموقف</h3>
              <p>
                علامة أزياء سعودية ناشئة في الرياض. حضور سوشيال ممتاز ومتابعون يقدّرون التصميم، لكن المتجر الإلكتروني يحوّل ١٫٢٪ فقط — أقل بـ ٣ مرات من متوسط القطاع. الإعلانات تستهلك الميزانية، والمبيعات لا تواكب.
              </p>
            </div>

            <div className="case-block">
              <h3 className="case-block-title">التشخيص</h3>
              <p>
                كشفت ورشة الاكتشاف ٣ مشاكل تشغيلية: صفحات منتجات بمعلومات ناقصة، عربة تسوّق بـ ٤ خطوات بدلًا من ٢، وغياب لقصة العلامة في الموقع. لم تكن المشكلة في المنتج — كانت في الترجمة الرقمية.
              </p>
            </div>

            <div className="case-block">
              <h3 className="case-block-title">الحل</h3>
              <p>
                أعدنا تصميم ٤٧ صفحة منتج بتدرّج معلوماتي مدروس (مقاس ← خامة ← استخدام ← قصة)، قلّصنا عربة التسوق إلى خطوتين، وأضفنا قسمًا تحريريًا «يوميات العلامة» يربط الزائر بالقصة قبل المنتج. أطلقنا التجارب على ٣ موجات أسبوعية مع قياس A/B لكل تغيير.
              </p>
            </div>

            <div className="case-results">
              <div className="result-block">
                <span className="result-value">+220%</span>
                <span className="result-label">معدل التحويل</span>
              </div>
              <div className="result-block">
                <span className="result-value">-31%</span>
                <span className="result-label">تكلفة اكتساب العميل</span>
              </div>
              <div className="result-block">
                <span className="result-value">+47%</span>
                <span className="result-label">قيمة الطلب</span>
              </div>
            </div>

            <blockquote className="case-quote">
              <p>«شعرنا أنهم فهموا علامتنا قبل أن نشرحها. النتائج تحدّثت عن نفسها — لكن الأهم أن طريقة التفكير تغيّرت في فريقنا أيضًا.»</p>
              <cite>— سارة العتيبي، مديرة التسويق</cite>
            </blockquote>
          </article>
        </div>
      </section>

      {/* ═══ 09 · TESTIMONIALS ═══ */}
      <section
        id="testimonials"
        aria-labelledby="testimonials-h"
        className="section-y relative"
      >
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x" style={{ maxWidth: "960px" }}>
          <span className="section-label">الآراء · ٠٩ / ١٤ — الأصوات</span>
          <h2 id="testimonials-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
            ثقة عملائنا هي إنجازنا الأكبر
          </h2>
          <p className="t-body-lg max-w-prose mb-12" style={{ color: "var(--text-secondary)" }}>
            آراء من{" "}
            <a href="#logos" style={{ color: "var(--accent-primary)" }}>أكثر من ٤٧ علامة سعودية</a> عملنا معها. أسماء حقيقية، نتائج موثّقة.
          </p>

          <ul className="flex flex-col" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {TESTIMONIALS.map((t) => (
              <li key={t.name} className="testimonial">
                <blockquote
                  className="t-body-lg"
                  style={{
                    color: "var(--text-primary)", maxWidth: "60ch",
                    margin: 0, paddingInlineStart: "32px",
                  }}
                >
                  {t.quote}
                </blockquote>
                <footer
                  className="testimonial-attribution mt-6"
                  style={{ paddingInlineStart: "32px" }}
                >
                  <div className="t-heading-3" style={{ color: "var(--text-primary)" }}>{t.name}</div>
                  <div className="t-label mt-2" style={{ color: "var(--text-tertiary)" }}>{t.role}</div>
                  {t.result && (
                    <div className="testimonial-result">
                      <span className="result-label">النتيجة</span>
                      <span className="result-value">{t.result}</span>
                    </div>
                  )}
                </footer>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══ 10 · TEAM / ABOUT ═══ */}
      <section id="team" aria-labelledby="team-h" className="section-y relative" style={{ background: "var(--bg-secondary)" }}>
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <span className="section-label">من نحن · ١٠ / ١٤ — الفريق</span>
            <h2 id="team-h" className="t-display-2 mt-4 mb-6" style={{ color: "var(--text-primary)" }}>
              من نحن
            </h2>
            <p className="t-body-lg" style={{ color: "var(--text-secondary)" }}>
              <strong style={{ color: "var(--text-primary)" }}>إتقان</strong> منصة سعودية متخصصة في إنشاء وإدارة المتاجر الإلكترونية والتسويق الرقمي. نجمع بين خبرة عميقة في السوق السعودي وإتقان تقني، فنُحوِّل أفكار رواد الأعمال إلى نجاح رقمي مستدام منذ ٢٠١٨. تعرّف على{" "}
              <a href="#awards" style={{ color: "var(--accent-primary)" }}>اعتماداتنا وشراكاتنا</a>.
            </p>
            <p className="founders-credibility">
              خبرة مجتمعة تتجاوز ١٢ عامًا في التجارة الرقمية السعودية.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <ul className="team-list">
              {FOUNDERS.map((f) => (
                <li key={f.name} className="team-member">
                  <span className="member-mark" aria-hidden="true">{f.n}</span>
                  <h3 className="member-name">{f.name}</h3>
                  <p className="member-role">{f.role}</p>
                  <p className="member-quote">{f.quote}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ═══ 11 · AWARDS & PRESS ═══ */}
      <section id="awards" aria-labelledby="awards-h" className="section-y relative">
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x">
          <header>
            <span className="section-label">الاعتمادات · ١١ / ١٤ — السلطة</span>
            <h2 id="awards-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
              شراكات معتمدة، نتائج موثّقة
            </h2>
            <p className="t-body-lg max-w-prose" style={{ color: "var(--text-secondary)" }}>
              اعتمادات حقيقية من منصات رسمية، وشراكات مُوثّقة منذ سنوات.
            </p>
          </header>

          <div className="awards-grid">
            <div className="award-block">
              <span className="award-category">شركاء معتمدون</span>
              <ul className="award-list">
                <li>سلة — شريك معتمد منذ ٢٠٢٠</li>
                <li>زد — شريك معتمد منذ ٢٠٢١</li>
                <li>Google Partner</li>
                <li>Meta Business Partner</li>
              </ul>
            </div>

            <div className="award-block">
              <span className="award-category">اعتمادات وعضويات</span>
              <ul className="award-list">
                <li>متوافق مع رؤية ٢٠٣٠ — التحول الرقمي</li>
                <li>سجل تجاري نشط في المملكة</li>
                <li>عضو مجتمع التجارة الإلكترونية السعودي</li>
              </ul>
            </div>

            <div className="award-block">
              <span className="award-category">حضور إعلامي</span>
              <ul className="award-list">
                <li>مشاركات في فعاليات Biban</li>
                <li>متحدثون في LEAP السنوي</li>
                <li>ضيوف بودكاست تجارة سعودية</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 13 · FAQ ═══ */}
      <section
        id="faq"
        aria-labelledby="faq-h"
        className="section-y relative"
        style={{ background: "var(--bg-secondary)" }}
      >
        <svg className="section-ornament" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
          <path d="M16 2 L20 12 L30 16 L20 20 L16 30 L12 20 L2 16 L12 12 Z"/>
          <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none"/>
        </svg>
        <div className="container-x" style={{ maxWidth: "880px" }}>
          <span className="section-label">الأسئلة · ١٣ / ١٤ — الإجابات</span>
          <h2 id="faq-h" className="t-display-2 mt-4 mb-4" style={{ color: "var(--text-primary)" }}>
            الأسئلة الشائعة
          </h2>
          <p className="t-body-lg max-w-prose mb-12" style={{ color: "var(--text-secondary)" }}>
            لم تجد إجابتك؟{" "}
            <a href="#contact" style={{ color: "var(--accent-primary)" }}>تواصل مع فريقنا مباشرة</a> ونعود إليك خلال ٢٤ ساعة.
          </p>

          <div role="list">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="faq-item"
                style={{ borderTop: i === 0 ? "1px solid var(--border-subtle)" : "none" }}
              >
                <summary className="t-heading-3">{f.q}</summary>
                <div className="faq-answer">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 14 · CONTACT — TRIMMED (header + 4-field form + direct list) ═══ */}
      <section
        id="contact"
        aria-labelledby="contact-h"
        className="contact"
      >
        <div className="contact-inner">

          <header className="contact-header">
            <span className="eyebrow-label">تواصل — الخطوة التالية</span>
            <h2 id="contact-h">نبدأ معك؟</h2>
            <p className="contact-intro">
              املأ النموذج وسنعود إليك خلال ٢٤ ساعة بخطوات عملية. للتواصل الفوري، اختر القناة المناسبة على الجانب.
            </p>
          </header>

          <div className="contact-grid">
            <form
              className="contact-form"
              method="POST"
              action="https://formspree.io/f/placeholder"
              noValidate
            >
              <div className="field">
                <label htmlFor="c-name">الاسم</label>
                <input
                  type="text"
                  id="c-name"
                  name="name"
                  required
                  autoComplete="name"
                  inputMode="text"
                  autoCapitalize="words"
                />
              </div>

              <div className="field">
                <label htmlFor="c-email">البريد الإلكتروني</label>
                <input
                  type="email"
                  id="c-email"
                  name="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  dir="ltr"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>

              <div className="field">
                <label htmlFor="c-phone">رقم الجوال</label>
                <input
                  type="tel"
                  id="c-phone"
                  name="phone"
                  required
                  autoComplete="tel"
                  inputMode="tel"
                  dir="ltr"
                  placeholder="+966 5x xxx xxxx"
                />
              </div>

              <div className="field">
                <label htmlFor="c-message">رسالتك</label>
                <textarea
                  id="c-message"
                  name="message"
                  rows={4}
                  required
                  placeholder="حدّثنا باختصار عن مشروعك أو سؤالك."
                />
              </div>

              <button type="submit" className="contact-submit">
                إرسال
                <span aria-hidden="true">←</span>
              </button>

              <p className="form-note">
                ↳ نرد خلال ٢٤ ساعة في أيام العمل · بياناتك محمية · <a href="/privacy">سياسة الخصوصية</a>
              </p>
            </form>

            <aside className="contact-direct" aria-label="قنوات التواصل المباشر">
              <a
                href="https://wa.me/966535632482"
                className="direct-method"
                dir="ltr"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="method-label">واتساب</span>
                <span className="method-value">+966 53 563 2482</span>
              </a>

              <a
                href="mailto:Care@itQan.Agency"
                className="direct-method"
                dir="ltr"
              >
                <span className="method-label">البريد الإلكتروني</span>
                <span className="method-value">Care@itQan.Agency</span>
              </a>

              <div className="direct-method direct-method-static">
                <span className="method-label">المكتب</span>
                <span className="method-value">الرياض، المملكة العربية السعودية</span>
              </div>

              <div className="direct-method direct-method-static">
                <span className="method-label">ساعات العمل</span>
                <span className="method-value">الأحد – الخميس · ٩ص – ٦م</span>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>

    {/* ═════════════════ FINAL CTA SECTION ═════════════════ */}
    <section className="final-cta-section" aria-labelledby="final-cta-h">
      <div className="final-cta-inner">
        <span className="final-cta-eyebrow">الخطوة الأخيرة</span>
        <h2 className="final-cta-heading" id="final-cta-h">جاهز تبدأ؟ نحن جاهزون.</h2>
        <p className="final-cta-sub">
          احجز استشارة مجانية، وسنعود إليك بخطة عمل أوّلية مكتوبة خلال ٢٤ ساعة عمل.
        </p>
        <div className="final-cta-actions">
          <a href="#contact" className="final-cta-btn">
            <svg className="btn-arrow" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M14 9H4M9 4l-5 5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            احجز استشارة الآن
          </a>
          <a
            href="https://wa.me/966535632482"
            target="_blank"
            rel="noopener noreferrer"
            className="final-cta-whatsapp"
            aria-label="تواصل عبر واتساب: +966 53 563 2482"
          >
            <span>أو تواصل عبر واتساب مباشرة</span>
            <span aria-hidden="true">←</span>
          </a>
        </div>
      </div>
    </section>


    {/* ═════════════════ FOOTER · Sky Floor (5-col elevated) ═════════════════ */}
    <footer role="contentinfo" className="site-footer elevated">
      <div className="footer-atmosphere" aria-hidden="true"></div>
      <div className="footer-watermark" aria-hidden="true"><span>إتقان</span></div>

      <div className="footer-top">
        <div className="footer-brand-wide">
          <a href="#main" className="footer-logo-lockup" aria-label="إتقان — العودة إلى أعلى الصفحة">
            <img
              src={itqanLogo}
              alt="إتقان"
              className="footer-logo-mark"
              width={120}
              height={120}
              loading="lazy"
              decoding="async"
            />
          </a>
          <p className="footer-tagline">
            منصة سعودية متخصصة في بناء وإدارة المتاجر الإلكترونية. نُطلق علاماتٍ تكبر منذ ٢٠١٨.
            أكثر من <strong>٤٬٧٠٠ مشروع ناجح</strong> في السعودية والخليج.
          </p>
          <p className="footer-hours">
            <span className="hours-dot" aria-hidden="true"></span>
            الأحد – الخميس · ٩ص – ٦م
          </p>
          <ul className="footer-social">
            <li><a href="https://twitter.com/itqanagency" target="_blank" rel="noopener noreferrer" aria-label="إكس">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a></li>
            <li><a href="https://instagram.com/itqanagency" target="_blank" rel="noopener noreferrer" aria-label="إنستغرام">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor"/>
              </svg>
            </a></li>
            <li><a href="https://linkedin.com/company/itqanagency" target="_blank" rel="noopener noreferrer" aria-label="لينكدإن">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.37V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.26 2.37 4.26 5.45zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56z"/>
              </svg>
            </a></li>
            <li><a href="https://youtube.com/@itqanagency" target="_blank" rel="noopener noreferrer" aria-label="يوتيوب">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6z"/>
              </svg>
            </a></li>
            <li><a href="https://wa.me/966535632482" target="_blank" rel="noopener noreferrer" aria-label="واتساب: +966 53 563 2482">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-1 1.1-.2.2-.4.2-.7.1-.3-.1-1.2-.4-2.4-1.5-.9-.8-1.5-1.8-1.7-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1.1 1-1.1 2.5 1.1 2.9 1.3 3.1c.2.2 2.2 3.4 5.3 4.7 2.6 1 3.1.8 3.7.8.6-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3M12 2C6.5 2 2 6.5 2 12c0 1.7.4 3.4 1.3 4.9L2 22l5.3-1.4c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2"/>
              </svg>
            </a></li>
          </ul>
        </div>

        <div className="footer-col">
          <h4 className="footer-heading">الخدمات</h4>
          <ul>
            <li><a href="#services">تصميم تجربة المستخدم</a></li>
            <li><a href="#services">استراتيجية رقمية</a></li>
            <li><a href="#services">إنشاء وإدارة المتاجر</a></li>
            <li><a href="#services">إعلانات الأداء</a></li>
            <li><a href="#services">تحسين معدل التحويل</a></li>
            <li><a href="#services">SEO تقني</a></li>
          </ul>
        </div>

        <div className="footer-col">
          <h4 className="footer-heading">المنصة</h4>
          <ul>
            <li><a href="#industries">القطاعات</a></li>
            <li><a href="#framework">الإطار</a></li>
            <li><a href="#portfolio">أعمالنا</a></li>
            <li><a href="#featured-case">دراسة حالة</a></li>
            <li><a href="#testimonials">آراء العملاء</a></li>
            <li><a href="#team">من نحن</a></li>
            <li><a href="#awards">الاعتمادات</a></li>
            <li><a href="#faq">الأسئلة</a></li>
          </ul>
        </div>

        <div className="footer-col footer-contact">
          <h4 className="footer-heading">تواصل</h4>
          <a
            href="tel:+966535632482"
            dir="ltr"
            className="footer-link footer-link-strong phone-link"
            aria-label="اتصال بإتقان: +966 53 563 2482"
          >+966 53 563 2482</a>
          <a
            href="https://wa.me/966535632482"
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            className="footer-link"
            aria-label="واتساب إتقان: +966 53 563 2482"
          >واتساب</a>
          <a
            href="mailto:Care@itQan.Agency"
            dir="ltr"
            className="footer-link"
          >Care@itQan.Agency</a>
          <p className="footer-address">الرياض، المملكة العربية السعودية</p>
        </div>
      </div>

      <div className="footer-trust">
        <span className="trust-item">شريك معتمد · سلة</span>
        <span className="trust-divider">·</span>
        <span className="trust-item">شريك معتمد · زد</span>
        <span className="trust-divider">·</span>
        <span className="trust-item">شريك معتمد · Google</span>
        <span className="trust-divider">·</span>
        <span className="trust-item">شريك معتمد · Meta</span>
        <span className="trust-divider">·</span>
        <span className="trust-item">معتمد رؤية ٢٠٣٠</span>
      </div>

      <div className="footer-bottom">
        <p className="copyright">© ٢٠٢٦ إتقان. جميع الحقوق محفوظة.</p>
        <ul className="footer-legal">
          <li><a href="/privacy">سياسة الخصوصية</a></li>
          <li><a href="/terms">الشروط والأحكام</a></li>
          <li><a href="/privacy#cookies">ملفات تعريف الارتباط</a></li>
        </ul>
        <p className="footer-credit">
          <span className="made-mark" aria-hidden="true">★</span>
          صُنع في الرياض · بإتقان
        </p>
      </div>
    </footer>

    {/* ═════════════════ FLOATING WHATSAPP (mobile-first, hidden on huge screens? — no: visible everywhere) ═════════════════ */}
    <a
      href={`https://wa.me/${siteConfig.whatsappNumber}?text=${encodeURIComponent("مرحبًا فريق إتقان، أرغب بالاستفسار")}`}
      className="float-whatsapp"
      dir="ltr"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="تواصل عبر واتساب"
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-1 1.1-.2.2-.4.2-.7.1-.3-.1-1.2-.4-2.4-1.5-.9-.8-1.5-1.8-1.7-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1.1 1-1.1 2.5 1.1 2.9 1.3 3.1c.2.2 2.2 3.4 5.3 4.7 2.6 1 3.1.8 3.7.8.6-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3M12 2C6.5 2 2 6.5 2 12c0 1.7.4 3.4 1.3 4.9L2 22l5.3-1.4c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2"/>
      </svg>
    </a>
  </>
);

export default StaticHome;
